import type { GeoPath, GeoProjection } from 'd3-geo';
import type { Feature, MultiPolygon, Polygon, Position } from 'geojson';

export const calculateLightSize = (users: number): number => {
	return users > 0 ? Math.max(6, Math.sqrt(users) * 1.5) : 0;
};

export const getLightColor = (index: number): string => {
	return index % 2 === 0 ? '#cf76e5' : '#e54edb';
};

export const getLightGradientId = (index: number): string => {
	return index % 2 === 0 ? 'lightGradientPink1' : 'lightGradientPink2';
};

type MapPoint = [number, number];

const isInsideRing = ([x, y]: MapPoint, ring: Position[]): boolean => {
	let inside = false;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const [xi, yi] = ring[i];
		const [xj, yj] = ring[j];
		if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
			inside = !inside;
		}
	}
	return inside;
};

export const getUserMarkers = (
	feature: Feature<Polygon | MultiPolygon>,
	users: number,
	pathGenerator: GeoPath,
	projection: GeoProjection
): { positions: MapPoint[]; size: number } => {
	if (users <= 0) return { positions: [], size: 0 };

	const count = users > 10 ? 1 : Math.floor(users);
	const [[minX, minY], [maxX, maxY]] = pathGenerator.bounds(feature);
	const centroid = pathGenerator.centroid(feature);
	if (![minX, minY, maxX, maxY, ...centroid].every(Number.isFinite)) {
		return { positions: [], size: 0 };
	}

	const polygons =
		feature.geometry.type === 'Polygon'
			? [feature.geometry.coordinates]
			: feature.geometry.coordinates;
	const contains = (point: MapPoint): boolean => {
		const coordinates = projection.invert?.(point);
		return (
			!!coordinates &&
			polygons.some(
				([outer, ...holes]) =>
					isInsideRing(coordinates, outer) && !holes.some((hole) => isInsideRing(coordinates, hole))
			)
		);
	};
	const size =
		users > 10 ? calculateLightSize(users) : Math.min(3, (maxX - minX) / 24, (maxY - minY) / 24);
	let margin = Math.min(maxX - minX, maxY - minY) / 8;
	const fitsInsideState = (point: MapPoint): boolean => {
		if (!contains(point)) return false;
		for (let index = 0; index < 16; index++) {
			const angle = (index * Math.PI) / 8;
			if (!contains([point[0] + Math.cos(angle) * margin, point[1] + Math.sin(angle) * margin])) {
				return false;
			}
		}
		return true;
	};
	if (count === 1 && fitsInsideState(centroid)) return { positions: [centroid], size };

	const interiorPoints: MapPoint[] = [];
	for (let row = 0; row < 32; row++) {
		for (let column = 0; column < 32; column++) {
			const point: MapPoint = [
				minX + ((column + 0.5) / 32) * (maxX - minX),
				minY + ((row + 0.5) / 32) * (maxY - minY),
			];
			if (contains(point)) interiorPoints.push(point);
		}
	}
	let candidates = interiorPoints.filter(fitsInsideState);
	for (let attempt = 0; candidates.length < count && attempt < 8; attempt++) {
		margin *= 0.75;
		candidates = interiorPoints.filter(fitsInsideState);
	}
	const distance = (a: MapPoint, b: MapPoint) => Math.hypot(a[0] - b[0], a[1] - b[1]);
	candidates.sort((a, b) => distance(a, centroid) - distance(b, centroid));
	const first = fitsInsideState(centroid) ? centroid : candidates[0];
	if (!first) return { positions: [], size: 0 };

	const positions: MapPoint[] = [first];
	while (positions.length < count && candidates.length) {
		let bestIndex = 0;
		let bestDistance = -1;
		candidates.forEach((candidate, index) => {
			const nearest = Math.min(...positions.map((point) => distance(point, candidate)));
			if (nearest > bestDistance) {
				bestDistance = nearest;
				bestIndex = index;
			}
		});
		positions.push(candidates.splice(bestIndex, 1)[0]);
	}
	return { positions, size };
};
